import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { referenceServers, thirdPartyServers, communityServers } from '../data/mcpData';

const Home = () => {
    const navigate = useNavigate();
    // Get only first 6 items
    const featuredItems = [...referenceServers.slice(0, 2), ...thirdPartyServers.slice(0, 2), ...communityServers.slice(0, 2)];

    // Get server statistics
    const getServerStats = () => {
        const referenceServersLen = referenceServers.length
        const thirdPartyServersLen = thirdPartyServers.length
        const communityServersLen = communityServers.length

        return { referenceServersLen, thirdPartyServersLen, communityServersLen };
    };

    const stats = getServerStats();

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-16 pb-16"
        >
            {/* Hero Section - Modern and Professional */}
            <section className="text-center py-20 relative overflow-hidden">
                <motion.div
                    className="absolute top-0 left-0 w-full h-full -z-10 opacity-30"
                    animate={{
                        background: [
                            'radial-gradient(circle at 20% 30%, rgba(77, 124, 254, 0.4) 0%, rgba(0, 0, 0, 0) 70%)',
                            'radial-gradient(circle at 80% 70%, rgba(77, 124, 254, 0.4) 0%, rgba(0, 0, 0, 0) 70%)',
                            'radial-gradient(circle at 20% 70%, rgba(36, 100, 255, 0.4) 0%, rgba(0, 0, 0, 0) 70%)',
                            'radial-gradient(circle at 80% 30%, rgba(36, 100, 255, 0.4) 0%, rgba(0, 0, 0, 0) 70%)',
                            'radial-gradient(circle at 20% 30%, rgba(77, 124, 254, 0.4) 0%, rgba(0, 0, 0, 0) 70%)',
                        ]
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                />

                {/* Floating circles decoration */}
                <div className="absolute inset-0 overflow-hidden -z-10">
                    {[...Array(6)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute rounded-full bg-blue-500 opacity-10"
                            style={{
                                width: Math.random() * 100 + 50,
                                height: Math.random() * 100 + 50,
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                            }}
                            animate={{
                                y: [0, -30, 0],
                                x: [0, Math.random() * 20 - 10, 0],
                            }}
                            transition={{
                                duration: 5 + Math.random() * 5,
                                repeat: Infinity,
                                delay: Math.random() * 5,
                            }}
                        />
                    ))}
                </div>

                <motion.h1
                    className="text-5xl md:text-6xl font-bold mb-6 glow-text tracking-tight"
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 100 }}
                >
                    <span className="primary-gradient">Model Context Protocol</span> <br />
                    {/* <span className="text-blue-600">Tool</span> */}
                </motion.h1>

                <motion.p
                    className="text-xl text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed"
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3, type: 'spring', stiffness: 100 }}
                >
                    Explore a curated collection of MCP servers to enhance the capabilities of your AI agents and improve AI application development efficiency.
                </motion.p>
            </section>

            {/* Stats Section */}
            <section className="py-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                    <motion.div
                        className="bg-white rounded-lg shadow-sm p-6 border border-gray-100 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    >
                        <Link to="/reference-servers">
                            <div className="text-3xl font-bold text-blue-600 mb-2">{stats.referenceServersLen}</div>
                            <div className="text-gray-600">Reference Servers</div>
                        </Link>
                    </motion.div>

                    <motion.div
                        className="bg-white rounded-lg shadow-sm p-6 border border-gray-100 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    >
                        <Link to="/thirdparty-servers">
                            <div className="text-3xl font-bold text-blue-600 mb-2">{stats.thirdPartyServersLen}</div>
                            <div className="text-gray-600">Third-Party Servers</div>
                        </Link>
                    </motion.div>

                    <motion.div
                        className="bg-white rounded-lg shadow-sm p-6 border border-gray-100 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    >
                        <Link to="/community-servers">
                            <div className="text-3xl font-bold text-blue-600 mb-2">{stats.communityServersLen}</div>
                            <div className="text-gray-600">Community Servers</div>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Featured MCP Servers */}
            <section className="py-8">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-800">
                        <span className="border-b-4 border-blue-500 pb-1">Featured Servers</span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {featuredItems.map((server, index) => (
                        <motion.div
                            key={server.name || index}
                            className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 hover:shadow-lg transition-shadow cursor-pointer"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 + (index * 0.1) }}
                            onClick={() => navigate(`/servers/${encodeURIComponent(server.name || '')}`)}
                        >
                            <div className="h-3 bg-gradient-to-r from-blue-500 to-blue-600"></div>
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-3">
                                    <h3 className="text-xl font-semibold text-gray-800 line-clamp-1">{server.name || "Unnamed Server"}</h3>
                                    <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">MCP</div>
                                </div>
                                <p className="text-gray-600 line-clamp-3 mb-4 h-18">{server.description || "No description"}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Features Section */}
            <section className="py-8 bg-gray-50 rounded-2xl p-8 max-w-6xl mx-auto">
                <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">MCP Features</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <motion.div
                        className="flex items-start"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <div className="bg-blue-100 p-3 rounded-lg mr-4">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Efficient Development</h3>
                            <p className="text-gray-600">Quickly build powerful AI applications with MCP servers, reducing repetitive development work.</p>
                        </div>
                    </motion.div>

                    <motion.div
                        className="flex items-start"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <div className="bg-blue-100 p-3 rounded-lg mr-4">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Safe and Reliable</h3>
                            <p className="text-gray-600">Selected MCP servers are tested and verified to ensure stability and security.</p>
                        </div>
                    </motion.div>

                    <motion.div
                        className="flex items-start"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className="bg-blue-100 p-3 rounded-lg mr-4">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Rich Ecosystem</h3>
                            <p className="text-gray-600">A variety of MCP servers meet different needs, from data processing to media generation.</p>
                        </div>
                    </motion.div>

                    <motion.div
                        className="flex items-start"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <div className="bg-blue-100 p-3 rounded-lg mr-4">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Standard Interface</h3>
                            <p className="text-gray-600">Standardized interfaces compliant with MCP specifications for easy integration and use.</p>
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div >
    );
};

export default Home;